import { useQuery } from "@tanstack/react-query";
import { debounce } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { getDeliveryPoint } from "../../http/basketApi";
import { fetchCitysForOrderModal } from "../../http/settingsApi";
import { Context } from "../../index";
import { formatDeliveryDate } from "../../utils/date";
import CitySelectionModal from "../SelectCity/SelectCity";
import AddressCard from "../UI/AddressCard/AddressCard";
import ButtonUI from "../UI/ButtonUI/ButtonUI";
import InputUI from "../UI/Input/Input";
import ModalBody from "../UI/ModalBody/ModalBody";
import ModalPanel from "../UI/ModalPanel/ModalPanel";
import Suggestions from "../UI/Suggestions/Suggestions";
import PickupDetail from "./PickupDetail";
import PickupMap from "./PickupMap";
import { Swiper, SwiperSlide } from "swiper/react";
import "./styles.scss";

export const DELIVERY_SERVICES = {
  SDEK: {
    type: "sdek_pvz",
    name: "СДЭК",
  },
  YANDEX: {
    type: "yandex_pvz",
    name: "Яндекс доставка"
  },
  SELF_PICKUP: {
    type: "show_room",
    name: "Магазины YouWanna",
  }
};

function usePickupPoints(city, enabled = true) {
  const queryKey = ["pickupPoints", city?.data?.id ?? city?.id ?? city?.name];
  const query = useQuery(queryKey, () => fetchDeliveryPoints(city), {
    enabled: enabled && Boolean(city),
    keepPreviousData: true,
  });
  return { ...query, queryKey };
}

export async function fetchDeliveryPoints(city) {
  const results = await getDeliveryPoint(city)

  return {points: results.reduce((acc, res) => {
      acc.push(res)
      return acc;
    }, [])};
}

export default function PickupBody({
  modalPanelTitle = "Пункты самовывоза",
  closeModal,
  selectPickup,
  children,
  onlySave,
  withSelectCity,
  pickupSource
}) {
  const isTablet = useMediaQuery('(max-width: 992px)')
  const { delivery, basket, user } = useContext(Context);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [activePoint, setActivePoint] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [cityForOrder, setCityForOrder] = useState(null);
  const [isMapModal, setIsMapModal] = useState(true);

  const handleSaveOrUpdate = async (data) => {
    const payload = {
      type: 'pickup',
      addressFields: {
        street: data.address,
        work_time: data.work_time,
        deliveryTerm: data.deliveryTerm,
        address_comment: data.address_comment,
        phone: data.phone,
        is_dressing_room: data.is_dressing_room,
      },
      pickupType: data.pickupType,
      city: delivery.cityInfo?.value
    };
  };

  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchValue(value);
    }, 300),
    []
  );

  const { data: ordinaryPoints = [] } = usePickupPoints(delivery.cityInfo, !pickupSource);
  const pickupPoints = pickupSource ? {points: pickupSource.points} : ordinaryPoints;

  const handleCitySelect = (item) => {
    if (!item.data) item = { value: item.name, data: item };
    delivery.setCityInfo(item);
    setIsCityModalOpen(false);
  };

  useEffect(() => {
    fetchCitysForOrderModal().then((response) => {
      if (!response.error) setCityForOrder(response);
    });
  }, []);

  const handleAddPickup = async () => {
    if (!ACTIVE_POINT_DATA) return;
    await handleSaveOrUpdate(ACTIVE_POINT_DATA);
  };

  const changePickupPoint = async () => {
    if (!ACTIVE_POINT_DATA) return;
    if (pickupSource) {
      if (pickupSource.pending || !await pickupSource.onSelect(ACTIVE_POINT_DATA)) return;
      if (selectPickup) selectPickup();
      return;
    }
    if (!onlySave) {
      delivery.setOfficePvz(ACTIVE_POINT_DATA);

      const deliveryMethod = delivery.allMethods.pickup.find(
        (method) => method.delivery_type === ACTIVE_POINT_DATA.type
      );

      delivery.setActive(deliveryMethod);
      delivery.setSelectedAddress(null);
    }
    //await handleAddPickup();
    if (selectPickup) selectPickup();
  };

  useEffect(() => {
    debouncedSetSearch(searchValue);
    return () => debouncedSetSearch.cancel();
  }, [searchValue, debouncedSetSearch]);

  useEffect(() => {
    setActivePoint(null);
  }, [serviceFilter]);

  const FILTERED_POINTS = useMemo(() => {
    return pickupPoints?.points ? pickupPoints.points.filter(
      (point) => !serviceFilter || point.type === serviceFilter
    ) : [];
  }, [pickupPoints.points, serviceFilter]);

  const SUGGESTIONS = useMemo(() => {
    if (!debouncedSearchValue) return [];
    const normalizedSearchValue = debouncedSearchValue.toLowerCase();
    return FILTERED_POINTS.filter((point) =>
      point.address.toLowerCase().includes(normalizedSearchValue)
    );
  }, [FILTERED_POINTS, debouncedSearchValue]);

  const ACTIVE_POINT_DATA = useMemo(() => {
    if (!activePoint) return null;
    return pickupPoints.points.find((point) => point.id === activePoint);
  }, [activePoint, pickupPoints.points]);

  useEffect(() => {
    return () => {
      delivery.setEditDataPickup(null)
    }
  }, [])

  const FILTERED_DELIVERY_SERVICES = useMemo(() => {

    let filtered = [];

    Object.values(DELIVERY_SERVICES).map((service) => {
       let points = pickupPoints?.points ? pickupPoints.points.filter(
           (point) => point.type === service.type
       ): []

      if(points.length > 0){
        filtered.push(service);
      }
    })

    return filtered;
  }, [pickupPoints.points]);

const PickupServices = () => {
  return (
    <Swiper
      slidesPerView="auto"
      spaceBetween={4}
    >
      {FILTERED_DELIVERY_SERVICES.map((service) => (
        <SwiperSlide
          key={service.type}
          style={{ width: "auto" }}
           breakpoints={{
           992: {
          slidesPerView: "auto",
          allowTouchMove: false,
          spaceBetween: 0,
        },
      }}
        >
          <button
            className={`pickup-services__button ${
              serviceFilter === service.type ? "_active" : ""
            }`}
            onClick={() => setServiceFilter(service.type)}
          >
            {service.name}
          </button>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

  const clearActivePoint = () => {
    setActivePoint(null)
  }

  const handleToggleTabs = () => {
    setIsMapModal((p) => !p)
  }

  return (
    <ModalBody>
      <div className="shipping-modal-wrapper">
        <div className="shipping-modal-header">
          <button
              className="mini-header__button"
              type="button"
              onClick={closeModal}
          >
            <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.8">
                <path
                    d="M26.25 32.5L13.75 20L26.25 7.5"
                    stroke="#1B1B1B"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>
          {isTablet ? ( 
            <div className="pickup-header-wrapper">
            <div className="pickup-tabs">
            <button className={`pickup-tabs__item ${isMapModal ? 'pickup-tabs__item--active' : ''}`}  onClick={handleToggleTabs}>На карте</button>
            <button className={`pickup-tabs__item ${!isMapModal ? 'pickup-tabs__item--active' : ''}`} onClick={handleToggleTabs}>Списком</button>
            </div>
             <PickupServices />  
          </div>) : (<span className="shipping-modal-header-name">Пункт самовывоза</span>)}
        </div>

        {
          !isTablet || isMapModal ? (        <div className="shipping-modal-wrapper__main">
          <PickupMap
            city={delivery.cityInfo}
            points={FILTERED_POINTS}
            onPlacemarkClick={(value) => {
              setActivePoint(value)
              setIsCityModalOpen(false)
            }}
          />
        </div>) : ( <div className="pickup-modal-mobile">
                   
                 <div className="pickup-modal__search">
                      <InputUI
                        text="Поиск по адресу"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        options={
                          debouncedSearchValue ? (
                            <Suggestions
                              query={debouncedSearchValue}
                              suggestions={SUGGESTIONS.map((point) => ({
                                text: `${point.service} | ${point.address}`,
                                id: point.id,
                              }))}
                              handleClick={(id) => {
                                setActivePoint(id);
                                setSearchValue("");
                              }}
                            />
                          ) : null
                        }
                      />
                    </div>
                       <div className="pickup-modal-mobile__wrapper"> 
                    {FILTERED_POINTS.map((point) => (
                      <div
                        key={`${point.service}-${point.id}`}
                        className="pickup-modal__item"
                        onClick={() => setActivePoint(point.id)}
                      >
                        <AddressCard
                          name={point.service}
                          address={point.address}
                          price={point.price}
                          date={point.deliveryTerm}
                          deliveryNotice={point.deliveryNotice}
                        />
                      </div>
                    ))}
                  </div>
          </div>
                )
        }

        { !isTablet && <div className="shipping-modal-wrapper__aside">
          <ModalPanel
            className="pickup-modal-panel"
            title={!activePoint && !isCityModalOpen ? modalPanelTitle : ""}
            hideClose={isCityModalOpen}
            onClose={isCityModalOpen ? () => setIsCityModalOpen(false) : closeModal}
            withoutOffset={!!activePoint || isCityModalOpen}
            slot={!activePoint ? <PickupServices /> : null}
          >
            {isCityModalOpen ? (
              <CitySelectionModal
                cityForOrder={cityForOrder}
                isOpen={isCityModalOpen}
                closeModal={() => setIsCityModalOpen(false)}
                setSityHandler={handleCitySelect}
              />
            ) : !activePoint ? (
              <div className="pickup-modal">
                <div className="pickup-modal__top">
                  {children}
                  {withSelectCity && (
                    <div className="delivery-form__wrapper">
                      <InputUI 
                        className="address-step__input" 
                        placeholder="Выберите населенный пункт" 
                        value={delivery.cityInfo?.value || ''} 
                        readOnly
                        onClick={() => setIsCityModalOpen(true)}
                      />
                      {!delivery.cityInfo && (
                        <span className="form-error">
                          Для выбора пункта выдачи необходимо указать населенный пункт
                        </span>
                      )}
                    </div>
                  )}
                  {delivery.cityInfo && (
                    <div className="pickup-modal__search">
                      <InputUI
                        text="Поиск по адресу"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        options={
                          debouncedSearchValue ? (
                            <Suggestions
                              query={debouncedSearchValue}
                              suggestions={SUGGESTIONS.map((point) => ({
                                text: `${point.service} | ${point.address}`,
                                id: point.id,
                              }))}
                              handleClick={(id) => {
                                setActivePoint(id);
                                setSearchValue("");
                              }}
                            />
                          ) : null
                        }
                      />
                    </div>
                  )}
                </div>
                {delivery.cityInfo && (
                  <div className="pickup-modal__bottom">
                    {FILTERED_POINTS.map((point) => (
                      <div
                        key={`${point.service}-${point.id}`}
                        className="pickup-modal__item"
                        onClick={() => setActivePoint(point.id)}
                      >
                        <AddressCard
                          name={point.service}
                          address={point.address}
                          price={point.price}
                          date={point.deliveryTerm}
                          deliveryNotice={point.deliveryNotice}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <PickupDetail
                data={ACTIVE_POINT_DATA}
                onClose={() => setActivePoint(null)}
                onClick={changePickupPoint}
                buttonText={"Заберу здесь"}
                disabled={pickupSource?.pending}
              />
            )}
          </ModalPanel>
        </div>}
        {isTablet && ACTIVE_POINT_DATA && <div className="shipping-modal-wrapper__card">
          <div className="shipping-modal-wrapper__card-top">
            <h3 className="shipping-modal-wrapper__card-service">
              {ACTIVE_POINT_DATA.service}
              <span>{ACTIVE_POINT_DATA.address}</span>
            </h3>
            <button className="shipping-modal-wrapper__card-close" type="button" onClick={clearActivePoint}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.8">
                  <path d="M6 18L18 6M6 6L18 18" stroke="#1B1B1B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </g>
              </svg>
            </button>
          </div>
          <div className="shipping-modal-wrapper__body">
            {
                (ACTIVE_POINT_DATA.deliveryNotice || ACTIVE_POINT_DATA.deliveryTerm) && (
                    <div className="pickup-detail__info pickup-detail__info--work-time">
                      <span className="pickup-detail__info-title">{ACTIVE_POINT_DATA.deliveryNotice ? 'Отправка' : 'Дата доставки'}</span>
                      <span className="pickup-detail__info-value">{ACTIVE_POINT_DATA.deliveryNotice || formatDeliveryDate(ACTIVE_POINT_DATA.deliveryTerm)}</span>
                    </div>
                )
            }

            <div className="pickup-detail__info pickup-detail__info--work-time">
              <span className="pickup-detail__info-title">Время работы</span>
              <span className="pickup-detail__info-value">{ACTIVE_POINT_DATA.work_time}</span>
            </div>

            {ACTIVE_POINT_DATA.phone && Array.isArray(ACTIVE_POINT_DATA.phone) && (
                <div className="pickup-detail__info">
                  <span className="pickup-detail__info-title">Телефон</span>
                  <span className="pickup-detail__info-value">
                            {ACTIVE_POINT_DATA.phone.map((phoneObj, index) => (
                                <span key={index}>
                                    <a href={`tel:${phoneObj.number}`}>
                                        {phoneObj.number}
                                    </a>
                                  {index < ACTIVE_POINT_DATA.phone.length - 1 && ', '}
                                </span>
                            ))}
                        </span>
                </div>
            )}

            {ACTIVE_POINT_DATA.address_comment && (
                <div className="pickup-detail__info">
                  <span className="pickup-detail__info-title">Как добраться</span>
                  <span className="pickup-detail__info-value">{ACTIVE_POINT_DATA.address_comment}</span>
                </div>
            )}

            {typeof ACTIVE_POINT_DATA.is_dressing_room === 'boolean' && !ACTIVE_POINT_DATA.is_dressing_room && (
                <span className="shipping-modal-wrapper__warn">Примерки нет</span>
            )}

          </div>
          <ButtonUI text={'Забрать отсюда'} onClick={changePickupPoint} disabled={pickupSource?.pending} />
        </div>}
      </div>
      {pickupSource?.pending && <p role="status">Обновляем доставку…</p>}
      {pickupSource?.error && <p role="alert">{pickupSource.error}</p>}
    </ModalBody>
  );
}
